import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Loader2, Eye, Crown } from 'lucide-react';
import { UsersTableProps } from '../types';

export function UsersTable({
  isDark,
  users,
  isLoading,
  onView,
  onEdit,
  onPremium,
  formatDate,
}: UsersTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn(
          isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
        )}>
          <TableHead className={cn(
            'font-semibold',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>ID</TableHead>
          <TableHead className={cn(
            'font-semibold',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>User</TableHead>
          <TableHead className={cn(
            'font-semibold hidden md:table-cell',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Email</TableHead>
          <TableHead className={cn(
            'font-semibold',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Role</TableHead>
          <TableHead className={cn(
            'font-semibold hidden lg:table-cell',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Status</TableHead>
          <TableHead className={cn(
            'font-semibold hidden sm:table-cell',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Created</TableHead>
          <TableHead className={cn(
            'font-semibold',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-12 text-slate-500">
              No users found
            </TableCell>
          </TableRow>
        ) : (
          users.map((user) => (
            <TableRow 
              key={user.id}
              className={cn(
                'transition-colors',
                isDark 
                  ? 'border-slate-700 hover:bg-slate-800/50' 
                  : 'border-slate-100 hover:bg-slate-50'
              )}
            >
              <TableCell className={cn(
                'font-mono text-xs',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )}>
                {user.id.substring(0, 8)}...
              </TableCell>
              <TableCell>
                <div>
                  <p className={cn(
                    'font-medium',
                    isDark ? 'text-white' : 'text-[#0F172A]'
                  )}>
                    {user.firstName && user.lastName 
                      ? `${user.firstName} ${user.lastName}`
                      : user.username}
                  </p>
                  <p className={cn(
                    'text-xs mt-0.5',
                    isDark ? 'text-slate-400' : 'text-slate-500'
                  )}>@{user.username}</p>
                </div>
              </TableCell>
              <TableCell className={cn(
                'hidden md:table-cell',
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>{user.email || '—'}</TableCell>
              <TableCell>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  user.role === 'admin'
                    ? 'bg-purple-500/10 text-purple-500'
                    : 'bg-blue-500/10 text-blue-500'
                )}>
                  {user.role}
                </Badge>
              </TableCell>
              <TableCell className="hidden lg:table-cell">
                <div className="flex flex-col gap-1">
                  <Badge className={cn(
                    'rounded-lg font-medium border-0 w-fit',
                    user.isSubscribed
                      ? 'bg-emerald-500/10 text-emerald-500'
                      : 'bg-slate-500/10 text-slate-500'
                  )}>
                    {user.isSubscribed ? 'Subscribed' : 'Free'}
                  </Badge>
                  {user.is_anonymous && (
                    <Badge className="rounded-lg font-medium border-0 w-fit bg-amber-500/10 text-amber-500">
                      Anonymous
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className={cn(
                'hidden sm:table-cell',
                isDark ? 'text-slate-400' : 'text-slate-500'
              )}>{formatDate(user.created_at)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onView(user)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    )}
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(user)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    )}
                    title="Edit user"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onPremium(user)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      user.isSubscribed
                        ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                        : isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-amber-400'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-amber-500'
                    )}
                    title={user.isSubscribed ? 'Manage premium' : 'Grant premium'}
                  >
                    <Crown className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
